import { OtpVerificationForm } from 'components/OtpVerificationModal/OtpVerificationForm';
import { Tab } from 'components/Tabs/Tab/Tab';
import { TabList } from 'components/Tabs/TabList/TabList';
import { TabPanel } from 'components/Tabs/TabPanel/TabPanel';
import { TabPanels } from 'components/Tabs/TabPanels/TabPanels';
import { Tabs } from 'components/Tabs/Tabs';
import { UserForgotPassword } from 'components/UserForgotPassword/UserForgotPassword';
import { UserLogin } from 'components/UserLogin/UserLogin';
import { UserSignup } from 'components/UserSignup/UserSignup';
import { Modal } from 'components/ui/Modal/Modal';
import { Logo } from 'containers/Logo/Logo';
import React, { useCallback, useEffect, useState } from 'react';
import { OtpChannel } from 'services/payex/otp/types';
import { useTranslations } from 'utils/translations/useTranslations';
import { LoginContextProvider } from '../../contexts/LoginContext/LoginContext';

const DELAY = 125;

export interface UserAccessModalProps {
  isOpen: boolean;
  loginOrigin?: string;
  onClose?: () => void;
  onLoginSuccess?: () => void;
  onSignUpSuccess?: () => void;
  showSignupTab?: boolean;
  allowClosing?: boolean;
  headerLogin?: React.ReactNode;
  headerSignUp?: React.ReactNode;
  hideSocialLogin?: boolean;
  showWeChatLogin?: boolean;
}

const UserAccessModal = ({
  isOpen,
  loginOrigin,
  onClose = () => null,
  onLoginSuccess = () => null,
  onSignUpSuccess = () => null,
  showSignupTab = false,
  allowClosing = true,
  headerLogin,
  headerSignUp,
  hideSocialLogin,
  showWeChatLogin = false,
}: UserAccessModalProps) => {
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showUpdatePasswordAlert, setShowUpdatePasswordAlert] = useState(false);

  const handleClose = () => {
    setShowResetPassword(false);
    onClose();
  };

  const onResetPassword = (showAlert = false) => {
    setShowResetPassword(prev => !prev);
    setShowUpdatePasswordAlert(showAlert);
  };

  return (
    <Modal isOpen={isOpen} allowClosing={allowClosing} onClose={handleClose}>
      <div
        className="textAlign-center marginBottom-md"
        data-testid="userAccessModal"
      >
        <Logo />
      </div>
      {showResetPassword ? (
        <UserForgotPassword
          onLogin={onResetPassword}
          showUpdatePasswordAlert={showUpdatePasswordAlert}
        />
      ) : (
        <UserAccessTabs
          loginOrigin={loginOrigin}
          onLoginSuccess={onLoginSuccess}
          onSignUpSuccess={onSignUpSuccess}
          showSignupTab={showSignupTab}
          onResetPassword={onResetPassword}
          headerLogin={headerLogin}
          headerSignUp={headerSignUp}
          hideSocialLogin={hideSocialLogin}
          showWeChatLogin={showWeChatLogin}
        />
      )}
    </Modal>
  );
};

interface UserAccessTabsProps {
  onLoginSuccess: () => void;
  onResetPassword: () => void;
  onSignUpSuccess: () => void;
  showSignupTab: boolean;
  loginOrigin?: string;
  headerLogin?: React.ReactNode;
  headerSignUp?: React.ReactNode;
  hideSocialLogin?: boolean;
  showWeChatLogin?: boolean;
}

const UserAccessTabs = ({
  onLoginSuccess,
  onSignUpSuccess,
  onResetPassword,
  showSignupTab,
  loginOrigin,
  headerLogin,
  headerSignUp,
  hideSocialLogin,
}: UserAccessTabsProps) => {
  const i18n = useTranslations();
  const loginTabIndex = 0;
  const signupTabIndex = 1;
  const defaultTab = showSignupTab ? signupTabIndex : loginTabIndex;
  const loginFirstInputRef = React.useRef<HTMLInputElement | null>(null);
  const signupFirstInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleOnTabChange = useCallback(({ index }: { index: number }) => {
    const textInput =
      index === loginTabIndex
        ? loginFirstInputRef.current
        : signupFirstInputRef.current;

    if (textInput) {
      setTimeout(() => textInput.focus(), DELAY);
    }
  }, []);

  useEffect(() => {
    handleOnTabChange({ index: loginTabIndex });
  }, [handleOnTabChange]);

  return (
    <LoginContextProvider loginOrigin={loginOrigin}>
      <Tabs defaultActiveIndex={defaultTab} onChange={handleOnTabChange}>
        <TabList>
          <Tab data-testid="userAccessModal-login">
            {i18n.t('userAccessModal.login')}
          </Tab>
          <Tab data-testid="userAccessModal-signup">
            {i18n.t('userAccessModal.signup')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {({ selectTab, isActive }) =>
              isActive && (
                <UserLogin
                  onLoginSuccess={onLoginSuccess}
                  handleSignUpClick={() => selectTab(signupTabIndex)}
                  resetPassword={onResetPassword}
                  firstInputRef={loginFirstInputRef}
                  header={headerLogin}
                  hideSocialLogin={hideSocialLogin}
                />
              )
            }
          </TabPanel>
          <TabPanel>
            {({ selectTab, isActive }) =>
              isActive && (
                <UserSignup
                  onSignUpSuccess={onSignUpSuccess}
                  handleLoginClick={() => selectTab(loginTabIndex)}
                  firstInputRef={signupFirstInputRef}
                  header={headerSignUp}
                  hideSocialLogin={hideSocialLogin}
                  renderOtp={props => (
                    <OtpVerificationForm
                      isActive={props.isOpen}
                      email={props.email}
                      senderName={props.senderName}
                      context={props.context}
                      channel={OtpChannel.Email}
                      onVerified={props.onVerified}
                      onBack={props.onClose}
                    />
                  )}
                />
              )
            }
          </TabPanel>
        </TabPanels>
      </Tabs>
    </LoginContextProvider>
  );
};

export { UserAccessModal };
